import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Phone, Mail, MapPin } from "lucide-react";
import { PROJECT } from "@/data/project";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const initial = {
  name: "",
  email: "",
  phone: "",
  business_type: "",
  local_interest: "",
  message: "",
};

const Contact = () => {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const validate = () => {
    if (form.name.trim().length < 2) return "Ingresa tu nombre.";
    if (!/^\S+@\S+\.\S+$/.test(form.email)) return "Ingresa un correo válido.";
    if (form.phone.trim().length < 5) return "Ingresa un teléfono válido.";
    return null;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, form);
      toast.success("¡Solicitud enviada! Te contactaremos en breve.");
      setForm(initial);
    } catch (e) {
      console.error(e);
      toast.error("No se pudo enviar la solicitud. Intenta nuevamente.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contacto"
      data-testid="contact-section"
      className="relative py-24 md:py-32 bg-[var(--co-bg)]"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-12">
        {/* Left — info */}
        <div className="lg:col-span-5">
          <span className="inline-flex items-center gap-2 text-[var(--co-accent)] uppercase tracking-[0.25em] text-xs font-semibold">
            <span className="h-px w-8 bg-[var(--co-accent)]" />
            06 · Reserva tu local
          </span>
          <h2 className="mt-6 font-display text-[var(--co-primary)] text-4xl md:text-5xl font-bold tracking-tight leading-[1.05]">
            Hablemos del
            <br />
            <span className="text-[var(--co-accent)]">próximo paso.</span>
          </h2>
          <p className="mt-6 text-[var(--co-text-muted)] text-lg leading-relaxed">
            Déjanos tus datos y un asesor comercial te contactará en menos de 24 horas con
            disponibilidad, precios y condiciones.
          </p>

          <div className="mt-10 space-y-6 border-t border-[var(--co-border)] pt-8">
            <a
              href={`tel:${PROJECT.contact.phoneRaw}`}
              data-testid="contact-phone"
              className="flex items-center gap-4 group"
            >
              <span className="w-10 h-10 border border-[var(--co-border)] flex items-center justify-center group-hover:border-[var(--co-accent)] group-hover:bg-[var(--co-accent)] transition-colors">
                <Phone className="w-4 h-4 text-[var(--co-primary)] group-hover:text-white transition-colors" />
              </span>
              <div>
                <p className="text-xs uppercase tracking-widest text-[var(--co-text-muted)]">
                  Teléfono
                </p>
                <p className="font-display font-bold text-[var(--co-primary)]">
                  {PROJECT.contact.phone}
                </p>
              </div>
            </a>

            <a
              href={`mailto:${PROJECT.contact.email}`}
              data-testid="contact-email"
              className="flex items-center gap-4 group"
            >
              <span className="w-10 h-10 border border-[var(--co-border)] flex items-center justify-center group-hover:border-[var(--co-accent)] group-hover:bg-[var(--co-accent)] transition-colors">
                <Mail className="w-4 h-4 text-[var(--co-primary)] group-hover:text-white transition-colors" />
              </span>
              <div>
                <p className="text-xs uppercase tracking-widest text-[var(--co-text-muted)]">
                  Correo
                </p>
                <p className="font-display font-bold text-[var(--co-primary)] break-all">
                  {PROJECT.contact.email}
                </p>
              </div>
            </a>

            <div className="flex items-start gap-4">
              <span className="w-10 h-10 border border-[var(--co-border)] flex items-center justify-center">
                <MapPin className="w-4 h-4 text-[var(--co-primary)]" />
              </span>
              <div>
                <p className="text-xs uppercase tracking-widest text-[var(--co-text-muted)]">
                  Oficina
                </p>
                <p className="font-display font-bold text-[var(--co-primary)]">
                  {PROJECT.contact.address}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Right — form */}
        <form
          onSubmit={onSubmit}
          data-testid="lead-form"
          className="lg:col-span-7 bg-white border border-[var(--co-border)] p-8 md:p-12"
        >
          <div className="grid sm:grid-cols-2 gap-6">
            <div>
              <label className="text-xs uppercase tracking-widest text-[var(--co-text-muted)] font-semibold">
                Nombre completo *
              </label>
              <Input
                data-testid="lead-name"
                value={form.name}
                onChange={(e) => setField("name", e.target.value)}
                placeholder="Tu nombre"
                className="mt-2 rounded-none border-0 border-b border-[var(--co-border)] bg-transparent px-0 focus-visible:ring-0 focus-visible:border-[var(--co-accent)] text-base h-12"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-[var(--co-text-muted)] font-semibold">
                Correo *
              </label>
              <Input
                data-testid="lead-email"
                type="email"
                value={form.email}
                onChange={(e) => setField("email", e.target.value)}
                placeholder="tu@correo.com"
                className="mt-2 rounded-none border-0 border-b border-[var(--co-border)] bg-transparent px-0 focus-visible:ring-0 focus-visible:border-[var(--co-accent)] text-base h-12"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-[var(--co-text-muted)] font-semibold">
                Teléfono *
              </label>
              <Input
                data-testid="lead-phone"
                value={form.phone}
                onChange={(e) => setField("phone", e.target.value)}
                placeholder="+507 ..."
                className="mt-2 rounded-none border-0 border-b border-[var(--co-border)] bg-transparent px-0 focus-visible:ring-0 focus-visible:border-[var(--co-accent)] text-base h-12"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-[var(--co-text-muted)] font-semibold">
                Tipo de negocio
              </label>
              <Select
                value={form.business_type}
                onValueChange={(v) => setField("business_type", v)}
              >
                <SelectTrigger
                  data-testid="lead-business-type"
                  className="mt-2 rounded-none border-0 border-b border-[var(--co-border)] bg-transparent px-0 focus:ring-0 h-12 text-base"
                >
                  <SelectValue placeholder="Selecciona una opción" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Restaurante">Restaurante / Cafetería</SelectItem>
                  <SelectItem value="Farmacia">Farmacia</SelectItem>
                  <SelectItem value="Clinica">Clínica / Salud</SelectItem>
                  <SelectItem value="Retail">Retail / Tienda</SelectItem>
                  <SelectItem value="Servicios">Servicios profesionales</SelectItem>
                  <SelectItem value="Banca">Banca / Fintech</SelectItem>
                  <SelectItem value="Otro">Otro</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2">
              <label className="text-xs uppercase tracking-widest text-[var(--co-text-muted)] font-semibold">
                Tamaño de local de interés
              </label>
              <Select
                value={form.local_interest}
                onValueChange={(v) => setField("local_interest", v)}
              >
                <SelectTrigger
                  data-testid="lead-local-interest"
                  className="mt-2 rounded-none border-0 border-b border-[var(--co-border)] bg-transparent px-0 focus:ring-0 h-12 text-base"
                >
                  <SelectValue placeholder="Selecciona un rango" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="50-70">50 – 70 m²</SelectItem>
                  <SelectItem value="70-100">70 – 100 m²</SelectItem>
                  <SelectItem value="100-200">100 – 200 m²</SelectItem>
                  <SelectItem value="Supermercado">Local ancla (1,779 m²)</SelectItem>
                  <SelectItem value="Sin definir">Aún no lo sé</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2">
              <label className="text-xs uppercase tracking-widest text-[var(--co-text-muted)] font-semibold">
                Mensaje (opcional)
              </label>
              <Textarea
                data-testid="lead-message"
                value={form.message}
                onChange={(e) => setField("message", e.target.value)}
                placeholder="Cuéntanos sobre tu negocio…"
                rows={4}
                className="mt-2 rounded-none border-0 border-b border-[var(--co-border)] bg-transparent px-0 focus-visible:ring-0 focus-visible:border-[var(--co-accent)] text-base resize-none"
              />
            </div>
          </div>

          <button
            data-testid="lead-submit"
            type="submit"
            disabled={loading}
            className="mt-10 w-full sm:w-auto inline-flex items-center justify-center gap-3 bg-[var(--co-primary)] hover:bg-[var(--co-primary-dark)] disabled:opacity-60 text-white px-10 py-4 text-sm font-bold uppercase tracking-[0.15em] transition-colors"
          >
            {loading ? "Enviando…" : "Solicitar información"}
          </button>

          <p className="mt-4 text-xs text-[var(--co-text-muted)]">
            Al enviar aceptas que un asesor de Viviendas de Primera te contacte sobre Costa Oeste Plaza.
          </p>
        </form>
      </div>
    </section>
  );
};

export default Contact;
